import Phaser from 'phaser';
import * as ABCya from '@abcya/abcya_phaser';
import ABCyaLib from '@abcya/abcya_lib';
import UIAssetKeys from '../UIAssetKeys';
import FontKeys from '../FontKeys';
import { getTotalRequiredShapes, REGIONS } from '../gameData';
import CubeButton, { CubeButtonEvents } from '../CubeButton';
import Game, { GameEvents, GameStates } from '../scenes/Game';
import SoundAssetKeys from '../SoundAssetKeys';

export default class CompletionScreen extends Phaser.GameObjects.Container {
    /**
     * Create the Completion Screen
     * @param {Game} scene the owner scene
     */
    constructor(scene) {
        super(scene);
        this.scene = scene;

        this.scene.add.existing(this);

        this._addLetters();
        this._addBadges();
        this._createHomeButton();
        this._createPrintButton();

        //this._addReference("Completion", "assets/reference_images/12 Get In Shape_Final Screen_1-01.jpg");
        //this._addReference("CompletionBW", "assets/reference_images/12 Get In Shape_Final Screen_BnW-01.jpg");
        //ABCya.Debug.enableImagePlacerDebug(this.scene, this.background);

        this.reset();

        this.scene.gameRt.drawFrame(ABCya.Images.DEFAULT_ATLAS, UIAssetKeys._13_Completion_Print___Black_and_White_FinishedBnW);
        this.scene.gameRt.snapshot(async image => {
            const capabilities = await ABCyaLib.Environment.instance.capabilities;
            if (capabilities.canPrintImages) {
                image = await ABCyaLib.Bitmap.rotateHTMLImage(image);
            }

            this.printImage = image;
        });
    }

    /**
     * Add a reference image to this state.
     * @param {string} key the unique key to use for this reference (in case you want to access it elsewhere)
     * @param {string} path the relative path to the reference image
     * @param {Boolean} enablePlacer whether to enable the image placer debug tool after adding the reference image
     */
    _addReference(key, path, enablePlacer = true) {
        ABCya.Debug.addScreenReference(this.scene, key, path, (image) => {
            if (enablePlacer) ABCya.Debug.enableImagePlacerDebug(this.scene, this.scene.background, image);
        });
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset() {
        this._sendTargetsOffScreen();
        this.setAlpha(0);

        // disable buttons
        this.homeButton.disableInteractive();
        this.printButton.disableInteractive();
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare() {
        this.setAlpha(1);
        this._sendTargetsOffScreen();

        // disable buttons

        this.homeButton.disableInteractive();
        this.printButton.disableInteractive();
    }

    /**
     * Animates the states in.
     */
    async begin() {
        ABCya.Audio.playOneShot(SoundAssetKeys._great_work, this.scene, 1, false, 0.5);
        ABCya.Audio.playSound(SoundAssetKeys._title_jingle, this.scene.titleScreen.jingleAudio, 1, true, 1);

        let tweens = [
            { targets: this.y0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.y0.onScreenPosition.y } },
            { targets: this.o0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.o0.onScreenPosition.y }, offset: 100 },
            { targets: this.u0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.u0.onScreenPosition.y }, offset: 200 },
            { targets: this.d0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.d0.onScreenPosition.y }, offset: 100 },
            { targets: this.i0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.i0.onScreenPosition.y }, offset: 200 },
            { targets: this.d1, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.d1.onScreenPosition.y }, offset: 300 },
            { targets: this.i1, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.i1.onScreenPosition.y }, offset: 200 },
            { targets: this.t, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.t.onScreenPosition.y }, offset: 300 },
            { targets: this.exclamationMark0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.exclamationMark0.onScreenPosition.y }, offset: 400 },
            { targets: this.a0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.a0.onScreenPosition.y }, offset: 0 },
            { targets: this.l0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.l0.onScreenPosition.y }, offset: 100 },
            { targets: this.l1, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.l1.onScreenPosition.y }, offset: 200 },
            { targets: this.s0, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.s0.onScreenPosition.y }, offset: 100 },
            { targets: this.h, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.h.onScreenPosition.y }, offset: 200 },
            { targets: this.a1, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.a1.onScreenPosition.y }, offset: 300 },
            { targets: this.p, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.p.onScreenPosition.y }, offset: 400 },
            { targets: this.e, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.e.onScreenPosition.y }, offset: 500 },
            { targets: this.s1, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.s1.onScreenPosition.y }, offset: 600 },
            { targets: this.f, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { y: this.f.onScreenPosition.y }, offset: 200 },
            { targets: this.o1, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { y: this.o1.onScreenPosition.y }, offset: 300 },
            { targets: this.u1, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { y: this.u1.onScreenPosition.y }, offset: 400 },
            { targets: this.n, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { y: this.n.onScreenPosition.y }, offset: 500 },
            { targets: this.d2, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { y: this.d2.onScreenPosition.y }, offset: 600 },
            { targets: this.exclamationMark1, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { y: this.exclamationMark1.onScreenPosition.y }, offset: 700 },
            { targets: this.beachBadge, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { scale: 1 }, offset: 100 },
            { targets: this.downtownBadge, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { scale: 1 }, offset: 300 },
            { targets: this.campBadge, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { scale: 1 }, offset: 500 },
            { targets: this.spaceBadge, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [1], props: { scale: 1 }, offset: 700 },
            { targets: this.homeButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 800 },
            { targets: this.printButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 0.83333 }, offset: 800 },
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () => {
                // enable buttons
                this.homeButton.setInteractive();
                this.printButton.setInteractive();
            }
        });

        let waitForTimeline = new Promise((resolve) => {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Animates the state out.
     */
    async leave() {
        // disable buttons
        this.homeButton.disableInteractive();
        this.printButton.disableInteractive();

        let tweens = [
            { targets: this, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 0 },
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
        });

        let waitForTimeline = new Promise((resolve) => {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Send all game objects to their start positions. Some game objects aren't sent "offscreen" but instead set to scale of 0 or alpha of 0.
     */
    _sendTargetsOffScreen() {
        this._sendTargetsOffScreenAll(
            [
                this.y0, this.o0, this.u0, this.d0, this.i0, this.d1, this.i1, this.t, this.exclamationMark0,
                this.a0, this.l0, this.l1, this.s0, this.h, this.a1, this.p, this.e, this.s1, this.f, this.o1,
                this.u1, this.n, this.d2, this.exclamationMark1
            ]);

        this.homeButton.setScale(0);
        this.printButton.setScale(0);

        this.beachBadge.setScale(0);
        this.downtownBadge.setScale(0);
        this.campBadge.setScale(0);
        this.spaceBadge.setScale(0);
    }

    /**
     * Sends all targets to their off screen positions.
     * @param {{offScreenPosition: {x:number, y:number}, onScreenPosition: {x:number, y:number}}[]} targets the array of targets to send to their offscreen position
     */
    _sendTargetsOffScreenAll(targets) {
        targets.forEach(target => {
            target.setPosition(target.offScreenPosition.x, target.offScreenPosition.y);
        });
    }

    /**
     * Adds the letter graphics.
     */
    _addLetters() {
        this.y0 = ABCya.Images.addImage(378, 99, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_Y, this);
        this.o0 = ABCya.Images.addImage(455, 104, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_O, this);
        this.u0 = ABCya.Images.addImage(534, 103, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_U, this);

        this.d0 = ABCya.Images.addImage(647, 104, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_D, this);
        this.i0 = ABCya.Images.addImage(703, 104, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_I, this);
        this.d1 = ABCya.Images.addImage(762, 103, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_D, this);

        this.i1 = ABCya.Images.addImage(854, 105, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_I, this);
        this.t = ABCya.Images.addImage(912, 99, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_T, this);
        this.exclamationMark0 = ABCya.Images.addImage(971, 105, UIAssetKeys._13_Completion_Color_Ltrs_You_Did_It_Exclmtn, this);

        this._setAnimationPositions([this.y0, this.o0, this.u0, this.d0, this.i0, this.d1, this.i1, this.t, this.exclamationMark0], { y: -190 });

        this.a0 = ABCya.Images.addImage(230, 561, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_A, this);
        this.l0 = ABCya.Images.addImage(295, 558, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_L, this);
        this.l1 = ABCya.Images.addImage(355, 559, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_L, this);

        this.s0 = ABCya.Images.addImage(448, 559, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_S, this);
        this.h = ABCya.Images.addImage(517, 558, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_H, this);
        this.a1 = ABCya.Images.addImage(587, 563, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_A, this);
        this.p = ABCya.Images.addImage(655, 548, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_P, this);
        this.e = ABCya.Images.addImage(724, 561, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_E, this);
        this.s1 = ABCya.Images.addImage(790, 559, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_S, this);

        this.f = ABCya.Images.addImage(357, 682, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_F, this);
        this.o1 = ABCya.Images.addImage(425, 690, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_O_1, this);
        this.u1 = ABCya.Images.addImage(494, 687, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_U_1, this);
        this.n = ABCya.Images.addImage(563, 689, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_N, this);
        this.d2 = ABCya.Images.addImage(632, 687, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_D_1, this);
        this.exclamationMark1 = ABCya.Images.addImage(684, 689, UIAssetKeys._13_Completion_Color_Ltrs_All_Shapes_Found_Exclmtn_1, this);

        this._setAnimationPositions([this.a0, this.l0, this.l1, this.s0, this.h, this.a1, this.p, this.e, this.s1, this.f, this.o1, this.u1, this.n, this.d2, this.exclamationMark1],
            { y: this.scene.scale.height + 190 });
    }

    /**
     * Adds the badge graphics.
     */
    _addBadges() {
        this.beachBadge = this._createBadge(REGIONS.BEACH, UIAssetKeys._13_Completion_Color_Bdge_Lg_Bch).setPosition(141, 324);
        this.downtownBadge = this._createBadge(REGIONS.DOWNTOWN, UIAssetKeys._13_Completion_Color_Bdge_Lg_City).setPosition(391, 324);
        this.campBadge = this._createBadge(REGIONS.CAMP, UIAssetKeys._13_Completion_Color_Bdge_Lg_Camp).setPosition(633, 324);
        this.spaceBadge = this._createBadge(REGIONS.SPACE, UIAssetKeys._13_Completion_Color_Bdge_Lg_Spc).setPosition(883, 324);
    }

    /**
     * Creates a new badge container, which consists of a badge icontext, and a checkmark.
     * @param {string} region the REGIONS enum value
     * @param {string} badgeKey the badge icon key from UIAssetKeys
     * @returns the badge container
     */
    _createBadge(region, badgeKey) {
        let container = this.scene.add.container();
        this.add(container);
        ABCya.Images.addImage(0, 0, badgeKey, container);
        ABCya.Images.addImage(91, -52, UIAssetKeys._13_Completion_Color_Chk_CmpltLg, container);
        ABCya.Images.addImage(83, 98, UIAssetKeys._13_Completion_Color_HldngShp_CmpltLg, container);
        ABCya.Text.addText(84, 99, 24, 0x2c2c89, FontKeys._graystrokeregular, getTotalRequiredShapes(region) + "/" + getTotalRequiredShapes(region), container);
        return container;
    }

    /**
     * Set the on/off screen positions. These are the start/end positions when the game object is animating in.
     * @param {Phaser.GameObjects.GameObject[]} targets the game objects to add the animation positions to
     * @param {{x:number, y:number}} offScreenPosition the offscreen position (the position the game object starts at when animating in)
     */
    _setAnimationPositions(targets, offScreenPosition) {
        targets.forEach(target => {
            let targetOffScreenPosition = { x: offScreenPosition.x ? offScreenPosition.x : target.x, y: offScreenPosition.y ? offScreenPosition.y : target.y };
            target.onScreenPosition = { x: target.x, y: target.y };
            target.offScreenPosition = targetOffScreenPosition;
        });
    }

    /**
     * Creates the home cube button and adds needed listeners.
     */
    _createHomeButton() {
        this.homeButton = new CubeButton(this.scene, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Home3, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Home2, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Home1);
        this.add(this.homeButton);
        this.homeButton.setPosition(90, 100);

        this.homeButton.on(CubeButtonEvents.CLICKED, () => this.scene.events.emit(GameEvents.GO_TO_STATE, GameStates.TITLE));
    }

    /**
     * Creates the print cube button and adds needed listeners.
     */
    _createPrintButton() {
        this.printButton = new CubeButton(this.scene, UIAssetKeys._Buttons_Print_Btn_Print3, UIAssetKeys._Buttons_Print_Btn_Print2, UIAssetKeys._Buttons_Print_Btn_Print1);
        this.add(this.printButton);
        this.printButton.setPosition(231, 100);
        this.printButton.scale = 0.83333

        this.printButton.on(CubeButtonEvents.CLICKED, async () => {
            this.homeButton.disableInteractive();
            this.printButton.disableInteractive();
            try {await ABCyaLib.PrintSave.instance.printImage(this.printImage);} catch{}
            this.homeButton.setInteractive();
            this.printButton.setInteractive();
        });
    }
}